import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {v4 as uuid} from 'uuid';
import {NotificationManager} from 'react-notifications';

import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Button from '../../components/Button';
import VirtualizedGrid from '../../components/VirtualizedGrid';
import DateTimeInput from '../../components/DateTimeInput';
import Input from '../../components/Input';
import AnswerInput from '../../components/AnswerInput';
import ProgressBar from '../../components/Progressbar';
import Notification from '../../components/Notification';
import VisibilityAnimation from '../../components/VisibilityAnimation';
import Loading from '../../components/Loading';
import { T } from '../../components/I18n';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';

class ImportStatusModal extends PureComponent {
  state = {
    loading: true,
    skipAll: false,
    catalog: []
  }

  async componentDidUpdate(prevProps) {
    const {form, status, credentials} = this.props;
    if (prevProps.status && !status) {
      this.setState({columns: null, data: null, loading: true});
    }
    if (prevProps.status && status && status.id === prevProps.status.id && prevProps.status.state === status.state) {
      return;
    }

    if (status && status.state === "COMPLETED_WITH_ERRORS" && !this.state.columns) {
      this.setState({loading: true}, async () => {
        const [failedRows, catalog] = await Promise.all([
          api.getFailedImport(status.id, credentials),
          api.getCatalog(credentials)
        ]);
        const worker = new Worker('/workers/import-data-worker.js');

        worker.onmessage = e => this.setState({...e.data, catalog});
        worker.postMessage([form, failedRows]);
      });
    }
    else if (this.state.loading) {
      this.setState({loading: false});
    }
  }

  completeImport() {
    const {data} = this.state;
    const {form, status, credentials, updateImportStatus} = this.props;

    this.setState({loading: true});

    const importRows = data.filter(datum => !datum[0].value).map(datum => {
      return {
        csvRow: datum[1].value,
        rowDate: datum[2].value,
        answers: datum.slice(3).reduce((map, ans) => {
          let theseAnswers = ans.value.map(v => ({
            id: uuid(),
            createdDate: new Date(),
            value: v,
            questionId: ans.question.id
          }));
          return [...map, ...theseAnswers];
        }, [])
      }
    });
    
    api.completeImport(status.id, importRows, credentials);
    const updatedStatus = {...status, state: "COMPLETING"};
    this.setState({columns: null, data: null}, () => updateImportStatus(updatedStatus, form));
  }

  changeValue(value, isValidAnswer, rowIndex, columnIndex) {
    const {data} = this.state;
    const newData = [...data];
    
    newData[rowIndex][columnIndex].value = value;
    newData[rowIndex][columnIndex].isValid = isValidAnswer;

    this.setState({data: newData, skipAll: false});
  }

  toggleEdit(value, rowIndex, columnIndex) {
    const {data} = this.state;
    const newData = [...data];
    
    newData[rowIndex][columnIndex].editing = value;
    newData[rowIndex][columnIndex].value = this.getDefaultSettings(newData[rowIndex][columnIndex]).value;
    newData[rowIndex][columnIndex].isValid = this.getDefaultSettings(newData[rowIndex][columnIndex]).valid;
  
    this.setState({data: newData, skipAll: false});
  }

  getDefaultSettings(cell) {
    const {question, value, isValid, type} = cell;
    if (isValid || type === 'DATE') {
      return {value, valid: false};
    }
    
    const options = question.questionOptions.sort((a, b) => a.order - b.order) || [];
    
    switch(question.type) {
      case 'BOOLEAN':
        return {value: ['true'], valid: true};

      case 'CHECKBOXES':
      case 'RADIO':
        return {value: [options[0].value], valid: true};
  
      default:
        return {value, valid: false};
    }
  }

  skipAll() {
    const {data, skipAll} = this.state;
    const skipState = !skipAll;

    const newData = data.map(datum => {
      const newPoint = [...datum];
      newPoint[0].value=skipState;
      return newPoint;
    });

    this.setState({data:newData, skipAll:skipState});
  }

  renderCell = (cell, rowIndex, columnIndex, column, measure) => {
    if (!cell.isValid && !cell.editing) {
      return <Input 
        id={`${rowIndex}-${columnIndex}`} 
        onClick={() => this.toggleEdit(!cell.editing, rowIndex, columnIndex)}
        type={"text"} 
        readOnly
        value={cell.value ? cell.value : ""} 
        invalid={true}/>
    }

    return this.editableCell(cell, rowIndex, columnIndex);
  }

  renderTitles = (column) => {
    if (column.key === "disabled") {
      return (<SkipButton key={column.key} width={column.width} onClick={() => this.skipAll()}>{column.title}</SkipButton>);
    }
    return (<GridHeaderTitle title={column.title}>{column.title ? column.title : <>&nbsp;</>}</GridHeaderTitle>);
  }

  editableCell = (cell, rowIndex, columnIndex) => {
    switch (cell.type) {
      case 'DATE':
        if (cell.isValid) {
          let date = new Date(cell.value);
          if (isNaN(date.getTime())) {
            date = new Date(parseInt(cell.value));
          }
          return <DateTimeInput 
            key={`cell-${rowIndex}-${columnIndex}`}
            date={date}
            onChange={d => this.changeValue(d.getTime(), true, rowIndex, columnIndex)}
          />
        }
        else {
          return <DateTimeInput 
            key={`cell-${rowIndex}-${columnIndex}`}
            date={new Date()}
            onChange={d => this.changeValue(d.getTime(), true, rowIndex, columnIndex)}
            invalid={!cell.isValid}
          />
        }
      
      case 'CSVROW':
        return cell.value;

      case 'ANSWER':
        return <AnswerInput
          key={`cell-${rowIndex}-${columnIndex}`}
          id={`answer-${cell.question.id}-${rowIndex}`}
          question={cell.question}
          values={cell.value}
          catalog={this.state.catalog}
          onChange={(value, isValidAnswer) => this.changeValue(value, isValidAnswer, rowIndex, columnIndex)} invalid={!cell.isValid}
        />

      case 'DISABLED':
        return (
          <CheckBox onClick={e => this.changeValue(!cell.value, true, rowIndex, columnIndex)} key={`cell-${rowIndex}-${columnIndex}`}>
            <input id={`${rowIndex}-${columnIndex}`} type="checkbox" checked={cell.value}/>
          </CheckBox>
        )

      default:
        throw new Error(`Unhandled cell type: ${cell.type}`);
    }
  }

  cancelImport() {
    const {status, form, onImportSuccess, credentials} = this.props;
    // eslint-disable-next-line
    if (confirm("Are you sure you want to cancel? All imported data will be lost!")) {
      //Assume this will work 100%, since it will take some time complete. 
      try {
        api.deleteImport(status.id, credentials);
        NotificationManager.success("Import successfully canceled");
        onImportSuccess({...status, state: "SIGNAL_STOP"}, form);
      }
      catch (e) {
        console.error(e);
        NotificationManager.error("Import cancel failed.");
      }
    }
  }

  closeModal() {
    const {onClose} = this.props;
    this.setState({failedRows: []}, () => onClose());
  }

  getOptions() {
    const {status} = this.props;
    const {loading} = this.state;

    if (!status) {
      return [];
    }

    if (status.state !== "COMPLETED_WITH_ERRORS") {
      return ([
        <Button type="submit" onClick={() => this.closeModal()} disabled={loading}>Okay</Button>,
        <Button secondary onClick={() => this.cancelImport()} disabled={loading || status.state === "SIGNAL_STOP" || status.state === "SIGNAL_STOPPED" || status.state === "COMPLETING"}>Cancel Import</Button>
      ])
    }

    return([
      <Button type="submit" onClick={() => this.completeImport()} disabled={loading || status.state !== "COMPLETED_WITH_ERRORS"}>Continue Import</Button>,
      <Button secondary onClick={() => this.cancelImport()} disabled={loading || status.state === "SIGNAL_STOP" || status.state === "SIGNAL_STOPPED" || status.state === "COMPLETING"}>Cancel Import</Button>
    ]);
  }

  render() {
    const {status} = this.props;
    const {loading} = this.state;

    if (!status) {
      return null;
    }

    return (
      <Form>
        <SideModal
          title={<T isolate>Import Data - <T variable="fileName">{status.fileName}</T></T>}
          open={status}
          onClose={() => this.closeModal()}
          options={this.getOptions()}
          large
        >
          <SideModal.Content>
            {loading && <Loading fullscreen />}
            {!loading && status.state === "COMPLETED_WITH_ERRORS" && this.renderGridView()}
            {!loading && status.state !== "COMPLETED_WITH_ERRORS" && this.renderProgressView()}
          </SideModal.Content>
        </SideModal>
      </Form>
    );
  }

  renderGridView() {
    const {columns, data} = this.state;
    if (!columns) {
      return null;
    }

    return (
      <>
        <Noti warning>
          {<T isolate><T variable="length">{data.length}</T> of your records could not be imported. You can manually fix them in the table below, or choose to skip it.</T>}
        </Noti>
        <Grid
          columns={columns}
          data={data}
          children={this.renderCell}
          titles={this.renderTitles}
          overrideListReset={true}
        />
      </>
    )
  }

  renderProgressView() {
    const {status} = this.props;
    const percentage = status.state === "SUCCESS" ? 100 : Math.floor((status.completedRows / status.totalRows) * 100);
    return (
      <Aligner>
        <Inner>
          <Message>{status.state !== "SUCCESS" ? `Your import is in progress...` : `Your import is complete`}</Message>
          <Counter>{`${percentage}%`}</Counter>
          <FlexBar>
            <ProgressBar percentage={percentage} />
          </FlexBar>
          {
            status.state === "PROCESSING_WITH_ERRORS" &&
            <ErrorMessage>
              <Notification warning>
                Some of your records could not be imported automatically. You can manually import them after the import completes.
              </Notification>
          </ErrorMessage>
          }
        </Inner>
      </Aligner>
    );
  }
}

const Grid = styled(VirtualizedGrid)`
  height: calc(85vh - 14rem);
`;

const Noti = styled(Notification)`
  margin: 20px 0;
`;

const CheckBox = styled.div`
  height: 100%;
  width: 100%;
  text-align: center;
  padding-top: 20%;
`;

const Aligner = styled.div`
  height: calc(85vh - 14rem);
  display: flex;
  align-items: center;
  flex-flow: row wrap;
  justify-content: center;
  padding: 0 10px;
`;

const Inner = styled.div`
  width: 50%;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  
  @media(max-width: 60rem) {
    width:100%;
  }
`;

const FlexBar = styled.div`
  order: 3;
  width: 100%;
  margin-top: 10px
`;

const Message = styled.div`
  order: 1;
`;

const Counter = styled.div`
  order: 2;
  text-align: right;
`;

const ErrorMessage = styled(props => <VisibilityAnimation visible duration="0.4s" show="fade-in" hide="fade-out" {...props} />)`
  width: 100%;
  order: 4;
  margin-top: 10px;
  display: flex;
  border-radius: 20px;
  align-items: center;
`;

const GridHeaderTitle = styled.h4`
  margin: 0;
  padding: 1rem 0 0 1rem;
  flex: 1;
  white-space: nowrap; 
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SkipButton = styled(props => <Button block blank {...props} />)`
  flex: 0 0 ${props => props.width}px;
  -ms-flex-preferred-size: calc(${props => props.width}px - 2rem - 1px);
  padding: 1rem 0 0 1rem;
  display: flex;
  align-items: center;
  line-height: 1.5;
  font-weight: bold;
`

export default withUser(ImportStatusModal);